import {
    faArrowRight,
    faChartLine,
    faCircleQuestion,
    faLink,
    faMoneyBillTransfer,
    faUsers,
    IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { Page } from '~/components/Page'
import { notch, Panel, PRIZE_GREEN } from '~/components/ui'
import { useViewer } from '~/features/auth'
import { SectionHeading, SectionSubheading } from '~/routes/landing/SectionHeading'

/**
 * The public affiliate pitch — `/affiliate`.
 *
 * Deliberately reachable signed out. It exists to be pasted into a creator's DMs, and a login wall
 * in front of a pitch spends the page's one job on a redirect. The CTA is the only thing that cares
 * whether you are signed in: with no account, applying starts at signup.
 *
 * Copy rule this page is held to: BR1 settles in USDC but is not sold as a crypto product, so the
 * money is described as a balance and a payout, never as a chain mechanic.
 */

/**
 * The page's call to action.
 *
 * An anchor carrying `.button-gradient`, not a `NavLink` inside `GradientButton` — the landing
 * page's `Buttons.tsx` documents why: that nesting is an anchor inside a button, which is invalid,
 * and it makes the hit area depend on which of the two elements received the padding.
 */
const Cta = ({ to, children, className = '' }: { to: string; children: ReactNode; className?: string }) => (
    <NavLink
        to={to}
        className={twMerge(
            'button-gradient button-gradient-animate flex min-h-[3rem] min-w-[14em] select-none items-center',
            'justify-center rounded-none px-[1.5em] py-[0.75em] text-center font-bold uppercase tracking-wide',
            'transition-transform hover:scale-[1.02] text-[clamp(0.875rem,1.4vw,1.25rem)]',
            className
        )}
    >
        {children}
    </NavLink>
)

/**
 * The two ways in, as two buttons on one page.
 *
 * Not two landing pages. The pitch is identical either way -- bring players, take a cut -- and
 * splitting it would mean maintaining two copies of the same page and deciding which URL to paste
 * into any given DM. What actually differs is one field on the application, so it belongs on a
 * button rather than on the link you hand out.
 *
 * Signed out, both go to signup first: `/affiliate/apply` is behind `RequireAuth`, and an
 * application is tied to a BR1 account, so aiming a visitor at the form would only bounce them.
 */
const ApplyButtons = ({ className = '' }: { className?: string }) => {
    const { data: user } = useViewer()
    const authed = user?.is_auth === true

    return (
        <div className={twMerge('flex flex-col items-center gap-3 sm:flex-row', className)}>
            <Cta to={authed ? '/affiliate/apply' : '/signup'}>
                {authed ? 'Apply as a creator' : 'Create an account to apply'}
            </Cta>
            {/*
              Outlined rather than a second gradient button. One primary call to action per view is
              the house rule, and creators are much the larger audience -- an agency knows which one
              it is and will find the quieter button.
            */}
            <NavLink
                to={authed ? '/affiliate/apply?as=agency' : '/signup'}
                className={twMerge(
                    'flex min-h-[3rem] min-w-[14em] select-none items-center justify-center',
                    'border border-white/25 px-[1.5em] py-[0.75em] text-center font-bold uppercase',
                    'tracking-wide transition-colors hover:border-white/60 hover:bg-white/5',
                    'text-[clamp(0.875rem,1.4vw,1.25rem)]'
                )}
            >
                Apply as an agency
            </NavLink>
        </div>
    )
}

/** The three headline terms. Uppercase label, big number, one line of qualification. */
const Term = ({ value, label, children }: { value: string; label: string; children: ReactNode }) => (
    <Panel size={14} className='flex h-full flex-col gap-2 p-5 md:p-6'>
        <span className='text-2xs font-bold uppercase tracking-[0.2em] text-white/40'>{label}</span>
        <span className='font-monument text-2xl font-bold leading-none md:text-3xl' style={{ color: PRIZE_GREEN }}>
            {value}
        </span>
        <p className='text-sm font-medium leading-relaxed text-white/60'>{children}</p>
    </Panel>
)

/** One numbered step in the How It Works band. */
const Step = ({ n, title, children }: { n: number; title: string; children: ReactNode }) => (
    <div className='flex flex-row gap-4'>
        <span
            className='flex h-9 w-9 shrink-0 items-center justify-center border border-[#03CEFF]/40 bg-black/60 font-black tabular-nums text-[#03CEFF]'
            style={{ clipPath: notch(8) }}
        >
            {n}
        </span>
        <div className='flex flex-col gap-1 pt-1'>
            <h3 className='text-sm font-black uppercase tracking-[0.15em]'>{title}</h3>
            <p className='text-sm font-medium leading-relaxed text-white/60'>{children}</p>
        </div>
    </div>
)

/** A capability of the affiliate dashboard. */
const Feature = ({ icon, title, children }: { icon: IconDefinition; title: string; children: ReactNode }) => (
    <div className='flex flex-row gap-3'>
        <FontAwesomeIcon icon={icon} className='mt-1 shrink-0 text-[#03CEFF]' />
        <div className='flex flex-col gap-1'>
            <h3 className='text-sm font-black uppercase tracking-[0.15em]'>{title}</h3>
            <p className='text-sm font-medium leading-relaxed text-white/60'>{children}</p>
        </div>
    </div>
)

/**
 * FAQ entries.
 *
 * Plain `<details>` rather than a controlled accordion — no state to hold, it works before
 * hydration, and it is searchable by the browser's own find-on-page, which a div-based accordion
 * is not.
 */
const FAQ_ITEMS: Array<{ q: string; a: ReactNode }> = [
    {
        q: 'What exactly does 25% apply to?',
        a: (
            <>
                The profit BR1 makes from the spawn fees paid by players you referred, after their winnings are
                paid back out. It is not 25% of what your players spend — it is 25% of what BR1 actually keeps.
                Your dashboard shows both figures so the number is never a mystery.
            </>
        ),
    },
    {
        q: 'How long do I earn from a player I refer?',
        a: (
            <>
                For as long as they play. A player is attributed to you permanently when they sign up through
                your link — there is no expiry window and no re-attribution to someone else later.
            </>
        ),
    },
    {
        q: 'When and how do I get paid?',
        a: (
            <>
                Monthly. Every statement is reviewed by a person before it is paid, so payouts are not instant —
                but each one is recorded against your account and shows up in your payout history.
            </>
        ),
    },
    {
        q: 'Do I need a BR1 account?',
        a: (
            <>
                Yes. Your referral link is tied to your username, and your dashboard and payouts live on your
                account. Creating one is free.
            </>
        ),
    },
    {
        q: 'Does everyone get accepted?',
        a: (
            <>
                No. Every application is read by a human. We are looking for people who can actually put BR1 in
                front of an audience — the size of that audience matters less than whether it is real.
            </>
        ),
    },
    {
        q: 'I manage streamers — is there something for me?',
        a: (
            <>
                Yes. Apply as an agency and you get a recruiting link to give your creators, a dashboard
                showing what each of them generates, and a share on top of theirs. Your creators earn exactly
                what an independent creator would — your cut comes from us, not out of their 25%.
            </>
        ),
    },
    {
        q: 'Are store purchases included?',
        a: (
            <>
                Not right now. The share currently covers gameplay only. If that changes we will tell every
                active affiliate before it takes effect, not after.
            </>
        ),
    },
]

const Faq = () => (
    <div className='flex flex-col gap-2'>
        {FAQ_ITEMS.map(({ q, a }) => (
            <Panel key={q} size={12}>
                <details className='group'>
                    <summary className='flex cursor-pointer list-none flex-row items-center justify-between gap-4 p-4 md:p-5'>
                        <span className='text-sm font-bold'>{q}</span>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className='shrink-0 text-white/40 transition-transform group-open:rotate-90'
                        />
                    </summary>
                    <p className='px-4 pb-4 text-sm font-medium leading-relaxed text-white/60 md:px-5 md:pb-5'>{a}</p>
                </details>
            </Panel>
        ))}
    </div>
)

/** A band: heading, optional subheading, content. The page is five of these stacked. */
const Band = ({
    heading,
    sub,
    children,
    className = '',
}: {
    heading: string
    sub?: string
    children: ReactNode
    className?: string
}) => (
    <section className={twMerge('flex flex-col gap-5 md:gap-7', className)}>
        <div className='flex flex-col gap-3'>
            <SectionHeading>{heading}</SectionHeading>
            {sub && <SectionSubheading>{sub}</SectionSubheading>}
        </div>
        {children}
    </section>
)

export const AffiliateLandingPage = () => (
    <Page title='Affiliate Program' header='Affiliate'>
        <div className='mx-auto flex w-full max-w-[1100px] flex-col gap-14 px-4 py-10 md:gap-20 md:px-8 md:py-16'>
            {/* Hero */}
            <section className='flex flex-col items-center gap-5 text-center'>
                <span
                    className='border border-[#03CEFF]/40 bg-black/70 px-3 py-1 text-2xs font-black uppercase tracking-[0.25em] text-[#03CEFF]'
                    style={{ clipPath: notch(6) }}
                >
                    Affiliate Program
                </span>
                <h1 className='px-3 text-center font-monument font-bold uppercase leading-tight text-[min(6.5vw,3.25rem)]'>
                    Bring players.
                    <br />
                    Take a cut.
                </h1>
                <SectionSubheading className='max-w-2xl'>
                    Send players to BR1 and earn 25% of the profit they generate — every month, for as long as they
                    keep playing.
                </SectionSubheading>
                <ApplyButtons className='mt-2' />
            </section>

            {/* The terms */}
            <div className='grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4'>
                <Term value='25%' label='Revenue share'>
                    A quarter of the profit BR1 makes from every player you bring.
                </Term>
                <Term value='Monthly' label='Payouts'>
                    Statements close monthly and are reviewed by a person before payment.
                </Term>
                <Term value='Lifetime' label='Attribution'>
                    A player you refer stays yours. No expiry window, no reassignment.
                </Term>
            </div>

            <Band heading='How it works' sub='Four steps, and only one of them is on you.'>
                <Panel>
                    <div className='flex flex-col gap-6 p-5 md:grid md:grid-cols-2 md:gap-8 md:p-8'>
                        <Step n={1} title='Apply'>
                            Tell us where your audience is and what you would do with BR1. It takes a couple of
                            minutes.
                        </Step>
                        <Step n={2} title='We review it'>
                            A person reads every application. You will hear back either way.
                        </Step>
                        <Step n={3} title='Share your link'>
                            Creators get a referral link for players. Agencies get a recruiting link for
                            creators, and a dashboard covering their whole roster.
                        </Step>
                        <Step n={4} title='Get paid'>
                            Your share accrues as your players play, and is paid out monthly.
                        </Step>
                    </div>
                </Panel>
            </Band>

            <Band
                heading='Your dashboard'
                sub='Approved affiliates get a tools page that answers the questions you would otherwise have to ask us.'
            >
                <Panel>
                    <div className='flex flex-col gap-6 p-5 md:grid md:grid-cols-2 md:gap-7 md:p-8'>
                        <Feature icon={faLink} title='Link performance'>
                            Visits to your link and how many of them became accounts.
                        </Feature>
                        <Feature icon={faUsers} title='Your players'>
                            Who signed up through you, when, and whether they are still playing.
                        </Feature>
                        <Feature icon={faChartLine} title='What you have earned'>
                            The profit your players generated and your 25% of it, for the current period.
                        </Feature>
                        <Feature icon={faMoneyBillTransfer} title='Payout history'>
                            Every payment we have made to you, so "have I been paid" is never a DM.
                        </Feature>
                    </div>
                </Panel>
            </Band>

            <Band
                heading='Who we want'
                sub='Reach helps. A real audience that trusts you helps more.'
            >
                <Panel>
                    <div className='flex flex-col gap-4 p-5 md:p-8'>
                        <p className='text-sm font-medium leading-relaxed text-white/70 md:text-base'>
                            Streamers, video makers, community organisers, guild leaders, and anyone already
                            talking to players who would enjoy a shooter they can actually win money at.
                        </p>
                        <p className='text-sm font-medium leading-relaxed text-white/70 md:text-base'>
                            You do not need a huge following. You do need an audience that is genuinely yours —
                            we can tell the difference, and so can the numbers on your dashboard.
                        </p>
                    </div>
                </Panel>
            </Band>

            <Band
                heading='Manage creators?'
                sub='Agencies get their own tier, and their own tools.'
            >
                <Panel>
                    <div className='flex flex-col gap-4 p-5 md:p-8'>
                        <p className='text-sm font-medium leading-relaxed text-white/70 md:text-base'>
                            If you represent a roster of streamers, apply as an agency instead. You get a
                            recruiting link to hand your creators, a dashboard showing what each of them is
                            generating, and a share on top of what they earn.
                        </p>
                        <p className='text-sm font-medium leading-relaxed text-white/70 md:text-base'>
                            Your creators keep their full 25% — an agency's cut comes from us, not from them.
                        </p>
                    </div>
                </Panel>
            </Band>

            <Band heading='Questions'>
                <div className='flex flex-col gap-4'>
                    <div className='flex flex-row items-center gap-2 text-white/40'>
                        <FontAwesomeIcon icon={faCircleQuestion} className='text-xs' />
                        <span className='text-2xs font-bold uppercase tracking-[0.2em]'>Before you apply</span>
                    </div>
                    <Faq />
                </div>
            </Band>

            {/* Closing CTA */}
            <section className='flex flex-col items-center gap-5 text-center'>
                <SectionHeading>Ready?</SectionHeading>
                <SectionSubheading className='max-w-xl'>
                    Applications are read by a person, usually within a few days.
                </SectionSubheading>
                <ApplyButtons />
            </section>
        </div>
    </Page>
)
